import { getOccupancyText } from 'components/Recommendations/utils/getOccupancyText'
import { OfferPageActions, OfferPageDispatchAction, OfferPageFilterTypes, OfferPageState } from 'contexts/OfferPage/offerPageStateReducer'
import { generateRoomOccupants } from 'lib/offer/occupancyUtils'
import { createSelector } from 'reselect'

import { DAY_FORMAT, DAY_MONTH_NAME_SHORT, DMY_CASUAL_SHORT_FORMAT } from 'constants/dateFormats'

export const getCurrenctOccupancyLabel = createSelector(
  (offerPageState: OfferPageState) => offerPageState.offer!,
  (offerPageState: OfferPageState) => offerPageState.rooms,
  (offer, rooms) => {
    const roomOccupants = rooms || [generateRoomOccupants()]
    return getOccupancyText(roomOccupants, offer.saleUnit)
  },
)

export const getCurrentDateLabel = createSelector(
  (offerPageState: OfferPageState) => offerPageState.checkInDate,
  (offerPageState: OfferPageState) => offerPageState.duration,
  (checkIn, duration) => {
    if (!checkIn) return ''

    const checkOut = checkIn.clone().add(duration, 'days')
    if (checkIn.isSame(checkOut, 'month')) {
      return `${checkIn.format(DAY_FORMAT)} - ${checkOut.format(DMY_CASUAL_SHORT_FORMAT)}`
    } else if (checkIn.isSame(checkOut, 'year')) {
      return `${checkIn.format(DAY_MONTH_NAME_SHORT)} - ${checkOut.format(DMY_CASUAL_SHORT_FORMAT)}`
    } else {
      return `${checkIn.format(DMY_CASUAL_SHORT_FORMAT)} - ${checkOut.format(DMY_CASUAL_SHORT_FORMAT)}`
    }
  },
)

export const getDatesOccupancyLabel = createSelector(
  getCurrentDateLabel,
  getCurrenctOccupancyLabel,
  (dateLabel, occupancyLabel) => {
    return `${dateLabel}, ${occupancyLabel}`
  },
)

export function flashHotelMobileVariantFilterMiddleware(
  state: OfferPageState,
  action: OfferPageDispatchAction,
  next: (state: OfferPageState) => OfferPageState,
) {
  if (action.type === OfferPageActions.filterContinue) {
    const hasRooms = !!state.rooms
    const hasDate = !!state.checkInDate
    const hasInvalidChildren = state.rooms?.some((room) =>
      room.childrenAge?.some((age) => age === -1),
    )
    /*
      Base filter flow is: Capacity -> Duration -> Check in -> Room -> Package option

      If there's no dates, we restart back at capacity
      Once we have dates, we go to room (or whatevers missing)
    */
    const nextState = next(state)
    let nextFilter: OfferPageFilterTypes | undefined = undefined
    if (hasInvalidChildren) {
      // invalid children, don't let them progress until dealt with
      nextFilter = 'capacity'
    } else if (state.activeFilter === 'capacity') {
      if (!hasRooms) {
        // user looked at capacity, didn't change anything and closed it
        // we consider this them applying the default rooms, so set rooms
        nextState.rooms = [generateRoomOccupants()]
      }

      if (!hasDate) {
        nextFilter = 'duration'
      }
    } else if (state.activeFilter === 'duration') {
      state.durationSelected = true
      nextFilter = 'checkin'
    } else if (state.activeFilter === 'checkin') {
      nextFilter = 'roomSelection'

      if (!hasRooms && hasDate) {
        nextFilter = 'capacity'
      }
    } else if (state.activeFilter === 'roomSelection') {
      nextFilter = 'packageSelection'
    }
    nextState.activeFilter = nextFilter
    return nextState
  } else if (action.type === OfferPageActions.filterBack) {
    const nextState = next(state)
    switch (state.activeFilter) {
      case 'capacity':
        // close the filter
        nextState.activeFilter = undefined
        break
      case 'duration':
        nextState.activeFilter = 'capacity'
        break
      case 'checkin':
        nextState.activeFilter = 'duration'
        break
      case 'roomSelection':
        nextState.activeFilter = 'checkin'
        break
      case 'packageSelection':
        nextState.activeFilter = 'roomSelection'
        break
    }
    return nextState
  }

  return state
}
